import { Tooltip } from 'antd';
import { NUTRITION_NO_REMOVAL_KEY_MESSAGE } from 'constants/messageConstants';
import {
  NUTRITION_STATUS_BALANCED,
  NUTRITION_STATUS_BUILDING,
  NUTRITION_STATUS_DECLINING,
  NUTRITION_STATUS_NO_REMOVAL_DATA,
} from 'constants/uiConstants';
import React from 'react';
import { LuArrowDown, LuArrowUp, LuEqual } from 'react-icons/lu';
import type { NutrientKey } from '../computeLiveTotals';
import {
  resolveNutrientStatus,
  type NutrientStatus,
} from './computeNutritionAudit';
import { formatKgPerHa, formatSignedKgPerHa } from './nutritionFormat';

export const NUTRIENT_STATUS_LABELS: Record<NutrientStatus, string> = {
  building: NUTRITION_STATUS_BUILDING,
  declining: NUTRITION_STATUS_DECLINING,
  balanced: NUTRITION_STATUS_BALANCED,
  noData: '',
  noRemovalData: NUTRITION_STATUS_NO_REMOVAL_DATA,
};

const STATUS_TEXT_CLASS: Record<NutrientStatus, string> = {
  building: 'text-success',
  declining: 'text-danger-dark',
  balanced: 'text-charcoal',
  noData: 'text-gray-muted',
  noRemovalData: 'text-gray-dark',
};

/** The arrow for a status — the colour never stands alone. */
export const NutrientStatusIcon: React.FC<{ status: NutrientStatus }> = ({
  status,
}) => {
  if (status === 'building') return <LuArrowUp size={12} aria-hidden />;
  if (status === 'declining') return <LuArrowDown size={12} aria-hidden />;
  if (status === 'balanced') return <LuEqual size={12} aria-hidden />;
  return null;
};

interface NutrientBalanceValueProps {
  nutrient: NutrientKey;
  applied: number;
  removed: number;
  balance: number;
}

/**
 * One nutrient's balance (kg/ha) with its status arrow and a screen-reader
 * status word. A nutrient crops record no removal for (Ni, Co) shows only
 * the applied amount, explained in a tooltip.
 */
const NutrientBalanceValue: React.FC<NutrientBalanceValueProps> = ({
  nutrient,
  applied,
  removed,
  balance,
}) => {
  const status = resolveNutrientStatus(nutrient, applied, removed, balance);

  if (status === 'noData') {
    return <span className={STATUS_TEXT_CLASS.noData}>—</span>;
  }

  if (status === 'noRemovalData') {
    return (
      <Tooltip title={NUTRITION_NO_REMOVAL_KEY_MESSAGE}>
        <span className={`cursor-help ${STATUS_TEXT_CLASS.noRemovalData}`}>
          {formatKgPerHa(applied)}
          <span className="sr-only">{` ${NUTRIENT_STATUS_LABELS[status]}`}</span>
        </span>
      </Tooltip>
    );
  }

  return (
    <span
      className={`inline-flex items-center justify-end gap-1 font-semibold ${STATUS_TEXT_CLASS[status]}`}
    >
      {formatSignedKgPerHa(balance)}
      <NutrientStatusIcon status={status} />
      <span className="sr-only">{NUTRIENT_STATUS_LABELS[status]}</span>
    </span>
  );
};

export default NutrientBalanceValue;
